import React from 'react';
import { Typography, Button, IconButton, Tooltip } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Save as SaveIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MonthlyShiftSlotSetupContainer from '../containers/MonthlyShiftSlotSetupContainer';
import { dailyShiftSlotRepository } from '../../../../infrastructure/repositories/DailyShiftSlotRepositoryImpl';
import { useMonthlyShiftSlotSetup } from '../hooks/useMonthlyShiftSlotSetup';
import { PageContainer, PageHeaderRow, MonthNavigation, HeaderButtonGroup } from '../MonthlyShiftSlotSetup.styles';
import '../../../../pages/ManagementList.css';

/**
 * 月次シフト枠設定ページ
 *
 * - レイアウト定義
 * - ヘッダー：戻る、月ナビゲーション、保存、アクションボタン（横一列）
 * - Container統合
 */
export const MonthlyShiftSlotSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const hookState = useMonthlyShiftSlotSetup(dailyShiftSlotRepository);
  const {
    data,
    isLoading,
    currentYear,
    currentMonth,
    hasUnsavedChanges,
    goToPreviousMonth,
    goToNextMonth,
    canGoToPreviousMonth,
    saveChanges,
    openBalancedConfirmDialog,
    handleExport,
    openDoctorSelectDialog,
    openImportDialog,
    openDateTimePickerDialog,
    openFixedShiftConfirmDialog,
    openFixedShiftCloseDialog,
    openUrgentTargetListModal,
  } = hookState;

  const handleBack = () => {
    navigate(-1);
  };

  const status = data?.status || 'draft';
  const isDraft = status === 'draft';
  const isFixedRecruiting = status === 'fixed_recruiting';
  const isFixedClosed = status === 'fixed_closed';
  const isFixedConfirmed = status === 'fixed_confirmed';
  const isRecruiting = status === 'recruiting';
  const isClosed = status === 'closed';
  const isConfirmed = status === 'confirmed';
  // 通常募集は draft と fixed_confirmed の両方から開始可能
  const canStartRecruitment = isDraft || isFixedConfirmed;

  return (
    <PageContainer>
      <div className="management-heading"><div><h1>月次シフト枠設定</h1><p>医師のシフト枠を月ごとに設定します</p></div></div>
      {/* ページヘッダー（全ボタン横一列） */}
      <PageHeaderRow>
        {/* 戻るボタン */}
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          size="small"
        >
          戻る
        </Button>

        {/* 月ナビゲーション */}
        <MonthNavigation>
          <IconButton
            onClick={goToPreviousMonth}
            disabled={isLoading || !canGoToPreviousMonth}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 100, textAlign: 'center' }}>
            {currentYear}年{String(currentMonth).padStart(2, '0')}月
          </Typography>
          <IconButton
            onClick={goToNextMonth}
            disabled={isLoading}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </MonthNavigation>

        {/* 保存ボタン */}
        <Tooltip title="変更を保存">
          <span>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SaveIcon />}
              onClick={saveChanges}
              disabled={!hasUnsavedChanges || isLoading || isConfirmed}
              size="small"
            >
              保存
            </Button>
          </span>
        </Tooltip>

        {/* アクションボタン群（区切り線付き） */}
        <HeaderButtonGroup>
          {/* 固定シフト募集ボタン */}
          <Tooltip
            title={
              isDraft
                ? '医師シフトルールを反映して固定シフト募集を開始する'
                : '下書き状態でのみ使用可能'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={() => openDateTimePickerDialog('fixedRecruitmentStart')}
                disabled={isLoading || !isDraft}
              >
                固定シフト募集
              </Button>
            </span>
          </Tooltip>

          {/* 固定シフト締切ボタン */}
          <Tooltip
            title={
              isFixedRecruiting
                ? '固定シフト募集（不可申告の受付）を締め切る'
                : '固定シフト募集中のみ使用可能'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={openFixedShiftCloseDialog}
                disabled={isLoading || !isFixedRecruiting}
              >
                固定シフト締切
              </Button>
            </span>
          </Tooltip>

          {/* 固定シフト確定ボタン */}
          <Tooltip
            title={
              isFixedClosed
                ? '医師ルール由来のシフトのみを確定する'
                : '固定シフト締切後のみ使用可能'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={openFixedShiftConfirmDialog}
                disabled={isLoading || !isFixedClosed}
              >
                固定シフト確定
              </Button>
            </span>
          </Tooltip>

          {/* 募集開始ボタン */}
          <Tooltip
            title={
              canStartRecruitment
                ? '募集を開始する'
                : '下書きまたは固定シフト確定後に使用可能'
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openDateTimePickerDialog('recruitmentStart')}
                disabled={isLoading || !canStartRecruitment}
              >
                募集開始
              </Button>
            </span>
          </Tooltip>

          {/* 締切ボタン */}
          <Tooltip title={isRecruiting ? '募集を締め切る' : '募集中状態でのみ使用可能'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openDateTimePickerDialog('deadline')}
                disabled={isLoading || !isRecruiting}
              >
                締切
              </Button>
            </span>
          </Tooltip>

          {/* 確定ボタン（バランス配分プレビュー → 本確定） */}
          <Tooltip
            title={
              isClosed
                ? '希望シフトをバランス配分して確定する（プレビューで確認してから確定）'
                : '締切状態でのみ使用可能'
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={openBalancedConfirmDialog}
                disabled={isLoading || !isClosed}
              >
                確定
              </Button>
            </span>
          </Tooltip>

          {/* 急募一覧ボタン */}
          <Tooltip title="急募一覧を開く">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={openUrgentTargetListModal}
                disabled={isLoading}
              >
                急募一覧
              </Button>
            </span>
          </Tooltip>

          {/* 空き枠紹介通知 */}
          <Tooltip title={isRecruiting ? '空き枠紹介通知を送信' : '募集中状態でのみ使用可能'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openDoctorSelectDialog('vacancy')}
                disabled={isLoading || !isRecruiting}
              >
                空き枠紹介通知
              </Button>
            </span>
          </Tooltip>

          {/* 急募枠通知 */}
          <Tooltip title={isRecruiting ? '急募枠通知を送信' : '募集中状態でのみ使用可能'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openDoctorSelectDialog('urgent')}
                disabled={isLoading || !isRecruiting}
              >
                急募枠通知
              </Button>
            </span>
          </Tooltip>

          {/* エクスポートボタン */}
          <Tooltip title="CSVエクスポート">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={isLoading}
              >
                エクスポート
              </Button>
            </span>
          </Tooltip>

          {/* インポートボタン */}
          <Tooltip title={isConfirmed ? '確定後はインポート不可' : 'CSVインポート'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileUploadIcon />}
                onClick={openImportDialog}
                disabled={isLoading || isConfirmed}
              >
                インポート
              </Button>
            </span>
          </Tooltip>
        </HeaderButtonGroup>
      </PageHeaderRow>

      {/* コンテナ */}
      <MonthlyShiftSlotSetupContainer hookState={hookState} />
    </PageContainer>
  );
};

export default MonthlyShiftSlotSetupPage;
